import React from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Switch } from '@/components/ui/switch'
import FactsListSection from './FactsListSection'
import { useMemorySettings } from '../hooks/useMemorySettings'
import type { MemorySettingsField } from '../types'

type Props = {
  workspaceId: string
  role: 'owner' | 'admin' | 'member'
}

const TIERS: { field: MemorySettingsField; label: string; description: string }[] = [
  {
    field: 'workspaceMemoryEnabled',
    label: 'Workspace memory',
    description: 'Shared team knowledge (accounts, ICP, process) Zeph learns and reuses across conversations.',
  },
  {
    field: 'userMemoryEnabled',
    label: 'Personal memory',
    description: "Each person's own preferences and context, learned from their conversations only.",
  },
  {
    field: 'semanticRecallEnabled',
    label: 'Conversation recall',
    description: 'Lets Zeph find and reference earlier conversations relevant to what’s being discussed now.',
  },
]

const MemorySettingsSection: React.FC<Props> = ({ workspaceId, role }) => {
  const { currentTheme: t } = useTheme()
  const canManageSettings = role === 'owner' || role === 'admin'
  // A member is forbidden from GET/PUT .../memory-settings — skip the fetch
  // entirely rather than surface a 403 as an error state (ticket 06: the tab
  // must still be reachable for a member's own facts review, below).
  const { settings, loading, error, retry, savingFields, fieldErrors, toggleTier } = useMemorySettings(
    workspaceId,
    canManageSettings,
  )

  return (
    <div className="flex flex-col gap-6">
      {canManageSettings && (
        <div className="flex flex-col gap-3">
          {loading && (
            <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading memory settings">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
              ))}
            </div>
          )}

          {!loading && (error || !settings) && (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <div
                className="flex h-14 w-14 items-center justify-center rounded-xl"
                style={{ backgroundColor: t.errorBackground }}
              >
                <AlertTriangle size={24} style={{ color: t.errorText }} />
              </div>
              <div>
                <p className="font-semibold text-base" style={{ color: t.text }}>Couldn&apos;t load memory settings</p>
                <p className="text-sm mt-1" style={{ color: t.mutedText }}>{error || 'Something went wrong.'}</p>
              </div>
              <button
                type="button"
                onClick={retry}
                className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
              >
                <RefreshCw size={14} />
                Retry
              </button>
            </div>
          )}

          {!loading && !error && settings && (
            <>
              <p className="text-[12.5px]" style={{ color: t.mutedText }}>
                Control what Zeph is allowed to remember and reuse in conversations. Turning a tier off stops Zeph
                from using it immediately &mdash; previously learned information is kept, not deleted.
              </p>

              <div className="flex flex-col gap-2">
                {TIERS.map(({ field, label, description }) => {
                  const checked = settings[field]
                  const saving = !!savingFields[field]
                  const fieldError = fieldErrors[field]
                  return (
                    <div
                      key={field}
                      className="rounded-xl p-4 flex flex-col gap-2"
                      style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <label htmlFor={`memory-tier-${field}`} className="text-sm font-semibold block" style={{ color: t.text }}>
                            {label}
                          </label>
                          <p className="text-[12.5px] mt-0.5" style={{ color: t.mutedText }}>
                            {description}
                          </p>
                        </div>
                        <Switch
                          id={`memory-tier-${field}`}
                          checked={checked}
                          disabled={saving}
                          onCheckedChange={() => void toggleTier(field)}
                          aria-label={label}
                        />
                      </div>
                      {saving && (
                        <span className="text-[12px]" style={{ color: t.mutedText }}>Saving&hellip;</span>
                      )}
                      {fieldError && (
                        <span className="text-[12px]" style={{ color: t.errorText }}>{fieldError}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}

      <FactsListSection workspaceId={workspaceId} role={role} />
    </div>
  )
}

export default React.memo(MemorySettingsSection)
